import { useState, useEffect, useRef } from 'react'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react'
import { guardianService } from '@/services/guardian'

interface ScanItem {
  app_slug: string
  app_name: string
  scan_status: string
  summary: any
  created_at: string
}

interface GuardianScanBannerProps {
  onScanComplete?: () => void
}

export function GuardianScanBanner({ onScanComplete }: GuardianScanBannerProps) {
  const [scans, setScans] = useState<ScanItem[]>([])
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const prevRunningRef = useRef(false)
  const completeFiredRef = useRef(false)
  const scanStartedRef = useRef(false)

  useEffect(() => {
    const poll = () => {
      guardianService.getScans()
        .then((data: any[]) => {
          // Only show scans from last 10 minutes
          const recent = data.filter(s => {
            const created = new Date(s.created_at)
            return Date.now() - created.getTime() < 600000
          })

          // Separate dependency scans from LLM analysis
          const depScans = recent.filter(s => s.scan_type === 'dependency')
          const llmScans = recent.filter(s => s.scan_type === 'llm_analysis')

          // Deduplicate dep scans by app_slug (keep most recent)
          const byApp = new Map<string, ScanItem>()
          for (const s of depScans) {
            if (!byApp.has(s.app_slug) || new Date(s.created_at) > new Date(byApp.get(s.app_slug)!.created_at)) {
              byApp.set(s.app_slug, s)
            }
          }

          // Add LLM analysis row — show it when dep scans are done
          const deduped = Array.from(byApp.values())
          const allDepsDone = depScans.length > 0 && depScans.every(s => s.scan_status === 'completed' || s.scan_status === 'failed')

          if (llmScans.length > 0) {
            deduped.push({ ...llmScans[0], app_slug: '__llm__', app_name: 'Refining results' })
          } else if (allDepsDone) {
            // LLM hasn't started yet but deps are done — show waiting row
            deduped.push({
              app_slug: '__llm__', app_name: 'Refining results',
              scan_status: 'running', summary: null, created_at: new Date().toISOString(),
            } as ScanItem)
          }
          setScans(deduped)

          // Check if anything is still running (including LLM)
          const hasRunning = recent.some(s => s.scan_status === 'running')
          const hasDepScans = depScans.length > 0
          const allDepsComplete = depScans.length > 0 && depScans.every(s => s.scan_status === 'completed' || s.scan_status === 'failed')
          const llmComplete = llmScans.length > 0 && llmScans[0].scan_status === 'completed'
          const fullCycleComplete = allDepsComplete && llmComplete

          if (hasRunning) {
            setVisible(true)
            setDismissed(false)
            scanStartedRef.current = true
            completeFiredRef.current = false
          }

          // Fire complete only when full cycle done (deps + LLM) and we haven't fired yet
          if (scanStartedRef.current && fullCycleComplete && !completeFiredRef.current) {
            completeFiredRef.current = true
            onScanComplete?.()
            setTimeout(() => setVisible(false), 20000)
          }

          // Also show banner if scans just completed (even if we missed the running state)
          if (hasDepScans && !scanStartedRef.current && !completeFiredRef.current) {
            const anyRecent = depScans.some(s => {
              const age = Date.now() - new Date(s.created_at).getTime()
              return age < 30000 // Created in last 30 seconds
            })
            if (anyRecent && (hasRunning || allDepsComplete)) {
              setVisible(true)
              scanStartedRef.current = true
            }
          }

          prevRunningRef.current = hasRunning
        })
        .catch(() => {})
    }

    poll()
    pollRef.current = setInterval(poll, 2500)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [onScanComplete])

  if (!visible || dismissed || scans.length === 0) return null

  const depScans = scans.filter(s => s.app_slug !== '__llm__')
  const llmScan = scans.find(s => s.app_slug === '__llm__')
  const completedCount = depScans.filter(s => s.scan_status === 'completed').length
  const failedCount = depScans.filter(s => s.scan_status === 'failed').length
  const depsRunning = depScans.some(s => s.scan_status === 'running')
  const allDepsComplete = depScans.length > 0 && depScans.every(s => s.scan_status !== 'running')
  const llmRunning = llmScan?.scan_status === 'running'
  const llmDone = llmScan?.scan_status === 'completed'
  const waitingForLlm = allDepsComplete && !llmDone
  const isRunning = depsRunning || llmRunning || waitingForLlm
  const totalDeps = depScans.reduce((sum, s) => {
    const summary = typeof s.summary === 'string' ? JSON.parse(s.summary) : s.summary
    return sum + (summary?.total_deps || 0)
  }, 0)

  return (
    <div className={`bg-white rounded-xl border p-5 transition-colors ${
      isRunning ? 'border-blue-200' : failedCount > 0 ? 'border-amber-200' : 'border-emerald-200'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {isRunning ? (
            <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
          ) : failedCount > 0 ? (
            <AlertCircle className="h-4 w-4 text-amber-600" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          )}
          <span className="font-semibold text-sm text-slate-900">
            {depsRunning
              ? `Scanning ${depScans.length} app${depScans.length > 1 ? 's' : ''}...`
              : (llmRunning || waitingForLlm)
              ? 'Refining results...'
              : 'Scan complete'
            }
          </span>
          {!isRunning && (
            <span className="text-xs text-slate-500">
              {completedCount} app{completedCount > 1 ? 's' : ''} scanned &middot; {totalDeps} dependencies checked
              {llmDone ? ' &middot; AI analysis applied' : ''}
            </span>
          )}
        </div>
        {!isRunning && (
          <button onClick={() => setDismissed(true)} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Per-app progress */}
      <div className="space-y-3">
        {scans.map(scan => {
          const summary = typeof scan.summary === 'string' ? JSON.parse(scan.summary) : scan.summary
          const isLlm = scan.app_slug === '__llm__'
          const step = summary?.step || 0
          const totalSteps = summary?.total_steps || 5
          const phase = summary?.phase || ''
          const pct = scan.scan_status === 'completed' ? 100 : scan.scan_status === 'failed' ? 0 : isLlm ? 50 : Math.round((step / totalSteps) * 100)
          const isComplete = scan.scan_status === 'completed'
          const isFailed = scan.scan_status === 'failed'

          return (
            <div key={scan.app_slug} className="flex items-center gap-4">
              <span className={`text-sm font-medium w-44 truncate ${isLlm ? 'text-blue-700' : 'text-slate-700'}`}>
                {scan.app_name}
              </span>
              <div className="flex-1">
                <Progress
                  value={pct}
                  className="h-2"
                  indicatorClassName={
                    isComplete ? (isLlm ? 'bg-blue-500' : 'bg-emerald-500') :
                    isFailed ? 'bg-red-400' :
                    isLlm ? 'bg-blue-400' :
                    'bg-blue-500'
                  }
                />
              </div>
              <div className="w-52 text-right">
                {isComplete && !isLlm && (
                  <span className="text-xs text-emerald-600 font-medium">
                    {summary?.total_deps || 0} deps &middot; {summary?.vulnerabilities || 0} vulns &middot; {summary?.outdated || 0} outdated
                  </span>
                )}
                {isComplete && isLlm && (
                  <span className="text-xs text-blue-600 font-medium">
                    {summary?.total_pass1_changes || 0} findings refined
                  </span>
                )}
                {isFailed && (
                  <Badge variant="outline" className="text-red-600 border-red-200 text-xs">Failed</Badge>
                )}
                {!isComplete && !isFailed && (
                  <span className="text-xs text-slate-500">{isLlm ? 'Filtering false positives...' : phase}</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
